/**
 * ItemCard Component
 *
 * A versatile card component that renders any of the 6 item types
 * in either grid or list view format.
 */

import { memo, type MouseEvent } from 'react';
import {
  Link2,
  Headphones,
  FileText,
  Lightbulb,
  File,
  CheckCircle2,
  Star,
  MoreVertical,
  ExternalLink,
  Edit2,
  Trash2,
  Eye,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { formatRelativeDate } from '@/utils/dates';
import { TagBadge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TaskCheckbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Item, ItemType, ViewMode, TaskItem } from '@/types';
import { isLinkItem, isPodcastItem, isNoteItem, isIdeaItem, isFileItem, isTaskItem } from '@/types';

// ============================================
// TYPES
// ============================================

export interface ItemCardProps {
  item: Item;
  view: ViewMode;
  onView: (item: Item) => void;
  onEdit: (item: Item) => void;
  onDelete: (item: Item) => void;
  onToggleFavorite: (item: Item) => void;
  onTagClick?: ((tag: string) => void) | undefined;
  onToggleComplete?: ((item: TaskItem, completed: boolean) => void) | undefined;
}

// ============================================
// ICON MAP
// ============================================

const typeIcons: Record<ItemType, typeof Link2> = {
  link: Link2,
  podcast: Headphones,
  note: FileText,
  idea: Lightbulb,
  file: File,
  task: CheckCircle2,
};

const typeColors: Record<ItemType, string> = {
  link: 'text-blue-600 bg-blue-50',
  podcast: 'text-purple-600 bg-purple-50',
  note: 'text-green-600 bg-green-50',
  idea: 'text-amber-600 bg-amber-50',
  file: 'text-gray-600 bg-gray-50',
  task: 'text-teal-600 bg-teal-50',
};

// ============================================
// HELPERS
// ============================================

/**
 * Get the thumbnail URL for an item
 */
function getThumbnail(item: Item): string | null {
  if (isLinkItem(item)) return item.thumbnail;
  if (isPodcastItem(item)) return item.thumbnail;
  return null;
}

/**
 * Get the description or preview for an item
 */
function getDescription(item: Item): string {
  if (item.description) return item.description;
  if (isNoteItem(item) || isIdeaItem(item)) return item.plainTextPreview;
  if (isLinkItem(item) || isPodcastItem(item)) return item.personalNotes;
  if (isFileItem(item)) return item.personalNotes || item.originalFileName;
  return '';
}

/**
 * Get the external URL for an item (if applicable)
 */
function getExternalUrl(item: Item): string | null {
  if (isLinkItem(item) || isPodcastItem(item)) return item.url;
  if (isFileItem(item)) return item.webViewLink;
  return null;
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ============================================
// SUBCOMPONENTS
// ============================================

interface TypeIconProps {
  type: ItemType;
  className?: string | undefined;
}

function TypeIcon({ type, className }: TypeIconProps) {
  const Icon = typeIcons[type];
  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-md p-1.5',
        typeColors[type],
        className
      )}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </div>
  );
}

interface ActionMenuProps {
  item: Item;
  onView: (item: Item) => void;
  onEdit: (item: Item) => void;
  onDelete: (item: Item) => void;
  onToggleFavorite: (item: Item) => void;
}

function ActionMenu({ item, onView, onEdit, onDelete, onToggleFavorite }: ActionMenuProps) {
  const externalUrl = getExternalUrl(item);

  const handleAction = (e: MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
          aria-label="Open item menu"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={(e) => handleAction(e as unknown as MouseEvent, () => onView(item))}
        >
          <Eye className="mr-2 h-4 w-4" />
          View Details
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => handleAction(e as unknown as MouseEvent, () => onEdit(item))}
        >
          <Edit2 className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>
        {externalUrl && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              window.open(externalUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Open Link
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={(e) => handleAction(e as unknown as MouseEvent, () => onToggleFavorite(item))}
        >
          <Star
            className={cn('mr-2 h-4 w-4', item.isFavorite && 'fill-amber-400 text-amber-400')}
          />
          {item.isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-red-600 focus:text-red-600"
          onClick={(e) => handleAction(e as unknown as MouseEvent, () => onDelete(item))}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ============================================
// GRID VIEW
// ============================================

type GridCardProps = Omit<ItemCardProps, 'view'>;

function GridCard({
  item,
  onView,
  onEdit,
  onDelete,
  onToggleFavorite,
  onTagClick,
  onToggleComplete,
}: GridCardProps) {
  const thumbnail = getThumbnail(item);
  const description = getDescription(item);
  const isTask = isTaskItem(item);

  const handleCardClick = () => {
    onView(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onView(item);
    }
  };

  return (
    <article
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-lg border border-gray-200',
        'bg-white shadow-sm transition-all duration-200 hover:shadow-md hover:border-gray-300 hover:scale-[1.02]',
        'focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-2',
        'cursor-pointer'
      )}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View ${item.title}`}
    >
      {/* Thumbnail */}
      {thumbnail && (
        <div className="relative aspect-video w-full overflow-hidden bg-gray-100">
          <img
            src={thumbnail}
            alt=""
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
          />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        {/* Header */}
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            {/* Task checkbox */}
            {isTask && onToggleComplete && (
              <div className="flex-shrink-0 mt-0.5" onClick={(e) => e.stopPropagation()}>
                <TaskCheckbox
                  checked={item.isCompleted}
                  onChange={(checked) => onToggleComplete(item, checked)}
                  aria-label={`Mark ${item.title} as ${item.isCompleted ? 'incomplete' : 'complete'}`}
                />
              </div>
            )}

            {/* Type icon (when no thumbnail) */}
            {!thumbnail && !isTask && <TypeIcon type={item.type} className="flex-shrink-0" />}

            {/* Title and favorite */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <h3
                  className={cn(
                    'font-medium text-gray-900 line-clamp-2',
                    isTask && item.isCompleted && 'line-through text-gray-500'
                  )}
                >
                  {item.title}
                </h3>
                {item.isFavorite && (
                  <Star
                    className="h-4 w-4 flex-shrink-0 fill-amber-400 text-amber-400"
                    aria-label="Favorited"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div
            className="opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
          >
            <ActionMenu
              item={item}
              onView={onView}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleFavorite={onToggleFavorite}
            />
          </div>
        </div>

        {/* Description */}
        {description && <p className="mb-3 text-sm text-gray-600 line-clamp-2">{description}</p>}

        {/* File info */}
        {isFileItem(item) && (
          <p className="mb-3 text-xs text-gray-500">
            {formatFileSize(item.fileSize)} &middot; {item.mimeType.split('/')[1]?.toUpperCase()}
          </p>
        )}

        {/* Footer: Tags and Date */}
        <div className="mt-auto flex flex-wrap items-center gap-2">
          {/* Tags */}
          {item.tags.slice(0, 3).map((tag) => (
            <TagBadge key={tag} tag={tag} onClick={onTagClick} size="sm" />
          ))}
          {item.tags.length > 3 && (
            <span className="text-xs text-gray-500">+{item.tags.length - 3}</span>
          )}

          {/* Date */}
          <span className="ml-auto text-xs text-gray-400">
            {formatRelativeDate(item.updatedAt)}
          </span>
        </div>
      </div>
    </article>
  );
}

// ============================================
// LIST VIEW
// ============================================

type ListCardProps = Omit<ItemCardProps, 'view'>;

function ListCard({
  item,
  onView,
  onEdit,
  onDelete,
  onToggleFavorite,
  onTagClick,
  onToggleComplete,
}: ListCardProps) {
  const isTask = isTaskItem(item);

  const handleCardClick = () => {
    onView(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onView(item);
    }
  };

  return (
    <article
      className={cn(
        'group flex items-center gap-4 rounded-lg border border-gray-200',
        'bg-white p-3 shadow-sm transition-all duration-200 hover:shadow-md hover:border-gray-300 hover:scale-[1.01]',
        'focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-2',
        'cursor-pointer'
      )}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View ${item.title}`}
    >
      {/* Task checkbox or Type icon */}
      {isTask && onToggleComplete ? (
        <div onClick={(e) => e.stopPropagation()}>
          <TaskCheckbox
            checked={item.isCompleted}
            onChange={(checked) => onToggleComplete(item, checked)}
            aria-label={`Mark ${item.title} as ${item.isCompleted ? 'incomplete' : 'complete'}`}
          />
        </div>
      ) : (
        <TypeIcon type={item.type} />
      )}

      {/* Title and description */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3
            className={cn(
              'font-medium text-gray-900 truncate',
              isTask && item.isCompleted && 'line-through text-gray-500'
            )}
          >
            {item.title}
          </h3>
          {item.isFavorite && (
            <Star
              className="h-4 w-4 flex-shrink-0 fill-amber-400 text-amber-400"
              aria-label="Favorited"
            />
          )}
        </div>

        {/* Tags (inline on list view) */}
        {item.tags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
            {item.tags.slice(0, 3).map((tag) => (
              <TagBadge key={tag} tag={tag} onClick={onTagClick} size="sm" />
            ))}
            {item.tags.length > 3 && (
              <span className="text-xs text-gray-500">+{item.tags.length - 3}</span>
            )}
          </div>
        )}
      </div>

      {/* Date */}
      <span className="hidden sm:block text-sm text-gray-400 flex-shrink-0">
        {formatRelativeDate(item.updatedAt)}
      </span>

      {/* Actions */}
      <div
        className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <ActionMenu
          item={item}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleFavorite={onToggleFavorite}
        />
      </div>
    </article>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

function ItemCardComponent({
  item,
  view,
  onView,
  onEdit,
  onDelete,
  onToggleFavorite,
  onTagClick,
  onToggleComplete,
}: ItemCardProps) {
  const CardComponent = view === 'grid' ? GridCard : ListCard;

  return (
    <CardComponent
      item={item}
      onView={onView}
      onEdit={onEdit}
      onDelete={onDelete}
      onToggleFavorite={onToggleFavorite}
      onTagClick={onTagClick}
      onToggleComplete={onToggleComplete}
    />
  );
}

// Memoize to prevent unnecessary re-renders
export const ItemCard = memo(ItemCardComponent);
